import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X } from 'lucide-react';
import { T, ROLES, springs } from '../../lib/tokens';
import { supabase } from '../../lib/supabase';

export const QuickCapture = ({ open, onClose, onUpdate }) => {
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const parseInput = (text) => {
    let title = text.trim();
    let priority = 2;
    let roleSlug = null;

    // Parse prefix: ! = P1
    if (title.startsWith('!')) {
      priority = 1;
      title = title.slice(1).trim();
    }

    // Parse @role
    const roleMatch = title.match(/@(liken|oneday|pryke|personal)\b/i);
    if (roleMatch) {
      roleSlug = roleMatch[1].toLowerCase();
      title = title.replace(roleMatch[0], '').trim();
    }

    return { title, priority, roleSlug };
  };

  const handleSubmit = async () => {
    if (!input.trim()) return;
    setSaving(true);

    const { title, priority, roleSlug } = parseInput(input);

    // Get role ID if specified
    let roleId = null;
    if (roleSlug) {
      const { data: roles } = await supabase.from('roles').select('id').eq('slug', roleSlug).single();
      if (roles) roleId = roles.id;
    }

    await supabase.from('tasks').insert({
      title,
      priority,
      role_id: roleId,
      done: false,
      effort: priority === 1 ? '1h' : '2h',
    });

    setInput('');
    setSaving(false);
    onUpdate();
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.15)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              zIndex: 998,
            }}
          />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={springs.snappy}
            style={{
              position: 'fixed',
              bottom: 80, right: 32,
              width: 400,
              background: 'rgba(255,255,255,0.96)',
              backdropFilter: 'blur(40px) saturate(200%)',
              WebkitBackdropFilter: 'blur(40px) saturate(200%)',
              border: '1px solid rgba(255,255,255,0.9)',
              borderRadius: 16,
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
              zIndex: 999,
              padding: '16px 20px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Plus size={14} color={T.amber} />
              <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>Quick Capture</span>
              <div style={{ flex: 1 }} />
              <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 2 }}>
                <X size={14} color={T.textMuted} />
              </button>
            </div>

            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleSubmit();
                if (e.key === 'Escape') onClose();
              }}
              placeholder="Type a task... (!urgent, @liken)"
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 10,
                fontSize: 14, border: '1px solid rgba(0,0,0,0.10)',
                background: 'rgba(255,255,255,0.8)',
                fontFamily: 'inherit', color: T.text, outline: 'none',
              }}
            />

            <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: T.textLight }}>
                <kbd style={{ background: 'rgba(0,0,0,0.04)', padding: '1px 4px', borderRadius: 3, fontSize: 10 }}>!</kbd> urgent
              </span>
              {Object.entries(ROLES).map(([slug, r]) => (
                <span key={slug} style={{ fontSize: 11, color: T.textLight }}>
                  <kbd style={{ background: 'rgba(0,0,0,0.04)', padding: '1px 4px', borderRadius: 3, fontSize: 10 }}>@{slug}</kbd>
                </span>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Floating action button for quick capture
export const QuickCaptureButton = ({ onClick }) => (
  <motion.button
    onClick={onClick}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    transition={springs.snappy}
    style={{
      position: 'fixed', bottom: 28, right: 28,
      width: 48, height: 48, borderRadius: '50%',
      background: T.amber, border: 'none', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 4px 16px rgba(212,146,30,0.4)',
      zIndex: 50,
    }}
  >
    <Plus size={20} color="#fff" />
  </motion.button>
);
